import { useEffect, useRef, useState } from 'react';
import { GridColDef, GridRowModel, useGridApiContext } from '@mui/x-data-grid';
import { Checkbox, ListItemText, MenuItem, Popover } from '@mui/material';
import ReusableDataGrid from '../ReusableDataGrid.tsx';
import { getAlerts, createAlert, updateAlert, deleteAlert, getMeasurements, getOsmLocations } from '../../services/api.ts';
import { DESC_MAP } from '../../utils/utils.ts';

const OPERATOR_OPTIONS = [
  { value: '>',  label: 'Above' },
  { value: '<',  label: 'Below' },
  { value: '>=', label: 'Above or equal' },
  { value: '<=', label: 'Below or equal' },
];

const defaultRow: Record<string, number | string | boolean | null | number[]> = {
  name: '',
  measurement_id: null,
  osm_ids: [],
  operator: '>',
  threshold: 0,
  duration_minutes: 5,
  isNew: true,
};

type OsmMultiSelectEditProps = {
  id: number | string;
  field: string;
  value: number[];
  osmOptions: { value: number | null; label: string }[];
};

function OsmMultiSelectEdit({ id, field, value, osmOptions }: OsmMultiSelectEditProps) {
  const apiRef = useGridApiContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  const selected: number[] = Array.isArray(value) ? value : [];
  const locationOptions = osmOptions.filter(o => o.value !== null) as { value: number; label: string }[];

  const handleToggle = (optValue: number) => {
    const newValue = selected.includes(optValue)
      ? selected.filter(v => v !== optValue)
      : [...selected, optValue];
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  const displayText = selected.length === 0
    ? 'All'
    : selected.map(id => locationOptions.find(o => o.value === id)?.label ?? id).join(', ');

  return (
    <>
      <div
        ref={cellRef}
        onClick={e => setAnchorEl(e.currentTarget)}
        style={{ width: '100%', cursor: 'pointer', padding: '0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace", fontSize: 13 }}
      >
        {displayText}
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        container={document.body}
        style={{ zIndex: 9999 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { background: 'var(--color-bg-secondary)', borderRadius: 2, minWidth: 220 } }}
      >
        <div style={{ maxHeight: 250, overflowY: 'auto' }}>
          {locationOptions.map(opt => (
            <MenuItem key={opt.value} onClick={() => handleToggle(opt.value)} sx={{ py: 0.5, fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
              <Checkbox
                checked={selected.includes(opt.value)}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.2)', '&.Mui-checked': { color: '#1D9E75' }, '& svg': { fontSize: '16px !important', padding: '0 !important' } }}
              />
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13, fontFamily: "'DM Mono', monospace" }} />
            </MenuItem>
          ))}
        </div>
      </Popover>
    </>
  );
}

export default function AlertsDataGrid() {
  const [initialRows, setInitialRows] = useState([]);
  const [measurementOptions, setMeasurementOptions] = useState<{ value: number; label: string }[]>([]);
  const [osmOptions, setOsmOptions] = useState<{ value: number | null; label: string }[]>([]);

  useEffect(() => {
    Promise.all([getAlerts(), getMeasurements(), getOsmLocations()]).then(([alerts, measurements, osms]) => {
      setInitialRows(alerts);
      setMeasurementOptions(
        measurements
          .filter((m: any) => !m.name.endsWith('_min') && !m.name.endsWith('_max'))
          .map((m: any) => ({ value: m.id, label: DESC_MAP[m.name] ?? m.name }))
      );
      setOsmOptions([
        { value: null, label: 'All' },
        ...osms.map((o: any) => ({ value: o.id, label: o.name })),
      ]);
    });
  }, []);

  const handleSaveRow = async (row: GridRowModel) => {
    const { isNew, id, ...fields } = row;
    const payload = { ...fields, osm_ids: fields.osm_ids?.length ? fields.osm_ids : null };
    if (isNew) {
      const [created] = await createAlert(payload as any);
      return { ...created, isNew: false };
    } else {
      const [updated] = await updateAlert(id, payload as any);
      return { ...updated, isNew: false };
    }
  };

  const handleDeleteRow = async (row: GridRowModel) => { await deleteAlert(row.id); };

  const columns: GridColDef[] = [
    { field: 'name',             headerName: 'Alert Name',      type: 'string',       flex: 1,    editable: true },
    { field: 'measurement_id',   headerName: 'Parameter',       type: 'singleSelect', flex: 1,    editable: true, valueOptions: measurementOptions },
    { field: 'operator',         headerName: 'Condition',       type: 'singleSelect', flex: 0.75, editable: true, valueOptions: OPERATOR_OPTIONS },
    { field: 'threshold',        headerName: 'Threshold Value', type: 'number',       flex: 0.75, editable: true },
    { field: 'duration_minutes', headerName: 'Duration (mins)', type: 'number',       flex: 0.75, editable: true },
    {
      field: 'osm_ids',
      headerName: 'Location',
      flex: 1,
      editable: true,
      valueFormatter: (value: number[]) => {
        if (!value?.length) return 'All';
        return value.map(id => osmOptions.find(o => o.value === id)?.label ?? id).join(', ');
      },
      renderEditCell: params => (
        <OsmMultiSelectEdit id={params.id} field={params.field} value={params.value ?? []} osmOptions={osmOptions} />
      ),
    },
  ];

  return (
    <ReusableDataGrid
      defaultRow={defaultRow}
      columns={columns}
      height="100%"
      width="100%"
      initialRows={initialRows}
      onSaveRow={handleSaveRow}
      onDeleteRow={handleDeleteRow}
    />
  );
}
